import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import investorStrategyService, {
  InvestorStrategyRecord,
  StrategyEntry,
  StrategyUpdateEntry,
  isSelfDirectedRecord,
} from "../services/investorStrategyService";
import type { FactorEntry } from "../services/productFactorsService";
import {
  ProductSearchResponse,
  productSearchService,
} from "../services/productSearchService";
import {
  productPerformanceService,
  type PerformanceProductType,
  type PerformanceResponse,
} from "../services/productPerformanceService";
import type { ProductSearchItem } from "../types/productV3";
import {
  GrossPositionCounts,
  ProductLookupEntry,
  ProductLookupKey,
  buildProductLookup,
  computeGrossPositionCounts,
} from "../utils/strategyUtils";
import {
  clipFactorsFromLaunch,
  computeCumulativePerformance,
  getV3AvailableFilters,
  getV3RangeForFilter,
  resolveLaunchClipMs,
  V3_FACTOR_SLOT_MS,
  V3TimeFilter,
} from "../components/performanceChartV3Utils";

export const HISTORY_PAGE_SIZE = 20;
const STRATEGY_FACTORS_PAGE_SIZE = 10000;
// Max the product-search routes accept; they clamp with Math.min(pageSize, 2000)
// rather than rejecting, so this is the fewest possible round trips for a full
// catalogue walk.
const PRODUCT_SEARCH_PAGE_SIZE = 2000;

export interface StrategyPerfMetrics {
  inception: number | null;
  returnToday: number | null;
  return1D: number | null;
  return1M: number | null;
  return3M: number | null;
}

export type StrategyIdProductMap = Map<number, { entityType: string; productId: number }>;

export interface UseStrategyDataResult {
  strategies: StrategyEntry[];
  /** Held entries the self-directed editors cannot render (e.g. managed strategies). */
  reservedEntries: InvestorStrategyRecord[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  productItems: ProductSearchItem[];
  allHistoryItems: StrategyUpdateEntry[];
  strategyIdProductMap: StrategyIdProductMap;
  strategyFactors: FactorEntry[];

  isLoadingStrategies: boolean;
  isLoadingProducts: boolean;
  isLoadingHistory: boolean;
  isLoadingFactors: boolean;
  isLoading: boolean;

  strategyError: string | null;
  historyError: string | null;
  factorsError: string | null;

  totalWeight: number;
  basketLookup: Map<number, ProductSearchItem>;
  positionCountsMap: Map<string, GrossPositionCounts>;
  strategyInceptionDate: string | null;
  strategyPerformanceStartDate: string | null;
  strategyPerfMetrics: StrategyPerfMetrics | null;
  perfByProduct: Record<string, PerformanceResponse | null>;

  refetchStrategies: () => void;
  refetchProducts: () => void;
  refetchCurrentTab: () => void;
}

/**
 * A product catalogue the caller already holds, injected instead of fetched.
 * Lets a parent that has paged the admin catalogue share it with a child
 * (e.g. the bulk-strategies page and the modal it opens) rather than each
 * paying for its own full page-walk of the same endpoint.
 */
export interface InjectedProductCatalogue {
  items: ProductSearchItem[];
  isLoading: boolean;
  error: string | null;
}

interface UseStrategyDataOptions {
  fetchProductPerformance?: boolean;
  /**
   * The user's `launch_date` (e.g. "2026/04/01"). When supplied and not in the
   * future, the customer's computed performance is anchored to it: every
   * pre-launch strategy factor is dropped so the returned `strategyFactors`,
   * inception/start dates, and `strategyPerfMetrics` all start at launch.
   * Callers pass the launch date of the user being shown (self or, for admin,
   * the viewed user); omitting it keeps full history.
   */
  launchDate?: string | null;
  /**
   * Strategy update history (`allHistoryItems`, `strategyIdProductMap`).
   * Defaults to true. Consumers that render no history pass false so the paged
   * `/updates` walk is never issued.
   */
  fetchHistory?: boolean;
  /**
   * Strategy performance factors (`strategyFactors`, inception/start dates,
   * `strategyPerfMetrics`). Defaults to true. Consumers that render no
   * performance pass false so the paged factor walk (pageSize 10000) is never
   * issued -- it is by far the most expensive fetch this hook makes.
   */
  fetchFactors?: boolean;
  /**
   * A catalogue the caller already has. When supplied, this hook does not fetch
   * products at all: `productItems`, `productLookup`, `basketLookup` and
   * `positionCountsMap` all derive from `products.items`, and
   * `isLoadingProducts` mirrors `products.isLoading`. `refetchProducts` becomes
   * a no-op -- refreshing an injected catalogue is the owner's job.
   */
  products?: InjectedProductCatalogue | null;
}

export function useStrategyData(userId?: string, options?: UseStrategyDataOptions): UseStrategyDataResult {
  // Opt-outs. Default to true so every existing caller keeps fetching exactly
  // what it does today; only a caller that renders none of this opts out.
  const historyEnabled = options?.fetchHistory ?? true;
  const factorsEnabled = options?.fetchFactors ?? true;
  const injectedProducts = options?.products ?? null;
  // Stable across renders even if the caller passes a fresh object literal, so
  // the fetch callbacks below keep their identity and the mount effect cannot
  // be re-triggered (which would refetch strategies) by an unmemoized prop.
  const productsInjected = injectedProducts !== null;

  // Strategy data
  const [strategies, setStrategies] = useState<StrategyEntry[]>([]);
  // Holdings this data set exposes but the self-directed editors cannot render.
  // They still consume the account's allocation budget, so an editor that gates
  // on a limit must subtract them or its budget is a lie.
  const [reservedEntries, setReservedEntries] = useState<InvestorStrategyRecord[]>([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(true);
  const [strategyError, setStrategyError] = useState<string | null>(null);

  // Product lookup
  const [productLookup, setProductLookup] = useState<Map<ProductLookupKey, ProductLookupEntry>>(
    new Map()
  );
  const [productItems, setProductItems] = useState<ProductSearchItem[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  // History
  const [allHistoryItems, setAllHistoryItems] = useState<StrategyUpdateEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Strategy performance factors (raw, full history; clipped below at launch)
  const [rawStrategyFactors, setRawStrategyFactors] = useState<FactorEntry[]>([]);
  const [isLoadingFactors, setIsLoadingFactors] = useState(true);
  const [factorsError, setFactorsError] = useState<string | null>(null);

  // Per-product performance (1D returns etc.)
  const [perfByProduct, setPerfByProduct] = useState<Record<string, PerformanceResponse | null>>({});

  const fetchIdRef = useRef(0);
  const productsFetchIdRef = useRef(0);
  const historyFetchIdRef = useRef(0);
  const factorsFetchIdRef = useRef(0);
  const hasLoadedStrategies = useRef(false);
  const hasLoadedProducts = useRef(false);
  const hasLoadedHistory = useRef(false);
  const hasLoadedFactors = useRef(false);
  const prevUserIdRef = useRef(userId);

  if (prevUserIdRef.current !== userId) {
    prevUserIdRef.current = userId;
    hasLoadedStrategies.current = false;
    hasLoadedProducts.current = false;
    hasLoadedHistory.current = false;
    hasLoadedFactors.current = false;
    ++fetchIdRef.current;
    ++productsFetchIdRef.current;
    ++historyFetchIdRef.current;
    ++factorsFetchIdRef.current;
    setStrategies([]);
    setReservedEntries([]);
    setAllHistoryItems([]);
    setRawStrategyFactors([]);
    setPerfByProduct({});
    setIsLoadingStrategies(true);
    setIsLoadingHistory(true);
    setIsLoadingFactors(true);
    setStrategyError(null);
    setHistoryError(null);
    setFactorsError(null);
  }

  const fetchStrategies = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setStrategyError(null);
    if (!hasLoadedStrategies.current) setIsLoadingStrategies(true);
    try {
      // Read the unfiltered records so we can expose both the self-directed
      // view (unchanged for every existing consumer) and the entries an editor
      // cannot render, which still consume the account's allocation budget.
      const records = userId
        ? await investorStrategyService.getStrategyRecordsForUser(userId)
        : await investorStrategyService.getStrategyRecords();
      if (fetchId !== fetchIdRef.current) return;
      // Both halves derive from the same predicate, so a future entity type the
      // editors cannot render lands in reservedEntries rather than being shown
      // as an editable self-directed row.
      setStrategies(records.filter(isSelfDirectedRecord));
      setReservedEntries(records.filter((record) => !isSelfDirectedRecord(record)));
      hasLoadedStrategies.current = true;
    } catch {
      if (fetchId !== fetchIdRef.current) return;
      setStrategyError("Failed to load strategies. Please try again.");
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoadingStrategies(false);
      }
    }
  }, [userId]);

  const fetchProducts = useCallback(async () => {
    // An injected catalogue is owned by the caller: never issue our own
    // page-walk of the same endpoint, and never leave the flag stuck on.
    if (productsInjected) {
      setIsLoadingProducts(false);
      return;
    }
    const fetchId = ++productsFetchIdRef.current;
    if (!hasLoadedProducts.current) setIsLoadingProducts(true);
    try {
      const fetchPage = (page: number) =>
        userId
          ? productSearchService.searchAdminProducts({ page, pageSize: PRODUCT_SEARCH_PAGE_SIZE })
          : productSearchService.searchProducts({ page, pageSize: PRODUCT_SEARCH_PAGE_SIZE });

      const firstPage = await fetchPage(1);
      if (fetchId !== productsFetchIdRef.current) return;
      let items: ProductSearchItem[] = firstPage.items;

      if (firstPage.totalPages > 1) {
        const remaining: Promise<ProductSearchResponse>[] = [];
        for (let p = 2; p <= firstPage.totalPages; p++) {
          remaining.push(fetchPage(p));
        }
        const pages = await Promise.all(remaining);
        if (fetchId !== productsFetchIdRef.current) return;
        for (const page of pages) {
          items = items.concat(page.items);
        }
      }

      setProductLookup(buildProductLookup(items));
      setProductItems(items);
      hasLoadedProducts.current = true;
    } catch {
      // Non-critical -- fallback names used
    } finally {
      if (fetchId === productsFetchIdRef.current) {
        setIsLoadingProducts(false);
      }
    }
  }, [userId, productsInjected]);

  const fetchHistory = useCallback(async () => {
    // Disabled by a caller that renders no history: clear the flag the
    // userId-change reset raised, so isLoadingHistory cannot strand at true.
    if (!historyEnabled) {
      setIsLoadingHistory(false);
      return;
    }
    const fetchId = ++historyFetchIdRef.current;
    setHistoryError(null);
    if (!hasLoadedHistory.current) setIsLoadingHistory(true);
    try {
      const fetchUpdates = userId
        ? (params: { page?: number; pageSize?: number }) =>
            investorStrategyService.getStrategyUpdatesForUser(userId, params)
        : (params: { page?: number; pageSize?: number }) =>
            investorStrategyService.getStrategyUpdates(params);

      const first = await fetchUpdates({
        page: 1,
        pageSize: HISTORY_PAGE_SIZE,
      });
      let items = first.items;
      if (first.totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: first.totalPages - 1 }, (_, i) =>
            fetchUpdates({ page: i + 2, pageSize: HISTORY_PAGE_SIZE })
          )
        );
        for (const p of remaining) items = items.concat(p.items);
      }
      if (fetchId !== historyFetchIdRef.current) return;
      setAllHistoryItems(items);
      hasLoadedHistory.current = true;
    } catch {
      if (fetchId !== historyFetchIdRef.current) return;
      setHistoryError("Failed to load history.");
    } finally {
      if (fetchId === historyFetchIdRef.current) {
        setIsLoadingHistory(false);
      }
    }
  }, [userId, historyEnabled]);

  const fetchStrategyFactors = useCallback(async () => {
    // Disabled by a caller that renders no performance. This is the hook's most
    // expensive fetch (paged, pageSize 10000), so skipping it matters most.
    if (!factorsEnabled) {
      setIsLoadingFactors(false);
      return;
    }
    const fetchId = ++factorsFetchIdRef.current;
    setFactorsError(null);
    if (!hasLoadedFactors.current) setIsLoadingFactors(true);
    try {
      const fetchFactors = userId
        ? (params: { page?: number; pageSize?: number }) =>
            investorStrategyService.getStrategyFactorsPageForUser(userId, params)
        : (params: { page?: number; pageSize?: number }) =>
            investorStrategyService.getStrategyFactorsPage(params);

      const firstPage = await fetchFactors({ pageSize: STRATEGY_FACTORS_PAGE_SIZE });
      let allItems = firstPage.items;
      if (firstPage.totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: firstPage.totalPages - 1 }, (_, i) =>
            fetchFactors({ page: i + 2, pageSize: STRATEGY_FACTORS_PAGE_SIZE })
          )
        );
        for (const page of remaining) allItems = allItems.concat(page.items);
      }
      allItems.sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
      if (fetchId !== factorsFetchIdRef.current) return;
      setRawStrategyFactors(allItems);
      hasLoadedFactors.current = true;
    } catch {
      if (fetchId !== factorsFetchIdRef.current) return;
      setFactorsError("Failed to load performance data.");
    } finally {
      if (fetchId === factorsFetchIdRef.current) {
        setIsLoadingFactors(false);
      }
    }
  }, [userId, factorsEnabled]);

  // Initial fetch on mount
  useEffect(() => {
    fetchStrategies();
    fetchProducts();
    fetchHistory();
    fetchStrategyFactors();
  }, [fetchStrategies, fetchProducts, fetchHistory, fetchStrategyFactors]);

  // Derived state

  // Anchor the customer's computed performance to their launch date when one is
  // configured (and not in the future): drop every pre-launch factor so the
  // strategy line, inception/start dates, and metrics all start at launch.
  // Opt-in via options.launchDate; callers that pass nothing keep full history.
  const launchClipMs = useMemo(
    () => resolveLaunchClipMs(options?.launchDate),
    [options?.launchDate]
  );
  const strategyFactors = useMemo(
    () => clipFactorsFromLaunch(rawStrategyFactors, launchClipMs),
    [rawStrategyFactors, launchClipMs]
  );

  const totalWeight = useMemo(
    () => strategies.reduce((sum, s) => sum + parseFloat(s.weight), 0),
    [strategies]
  );

  // Single source for every product-derived value below: the injected catalogue
  // when the caller owns one, otherwise what this hook fetched itself.
  const resolvedProductItems = useMemo(
    () => (injectedProducts ? injectedProducts.items : productItems),
    [injectedProducts, productItems]
  );
  const resolvedProductLookup = useMemo(
    () => (injectedProducts ? buildProductLookup(injectedProducts.items) : productLookup),
    [injectedProducts, productLookup]
  );
  const resolvedIsLoadingProducts = injectedProducts
    ? injectedProducts.isLoading
    : isLoadingProducts;

  const basketLookup = useMemo(() => {
    const map = new Map<number, ProductSearchItem>();
    for (const item of resolvedProductItems) {
      if (item.productType === "BASKET") {
        map.set(item.productId, item);
      }
    }
    return map;
  }, [resolvedProductItems]);

  const positionCountsMap = useMemo(() => {
    const map = new Map<string, GrossPositionCounts>();
    for (const entry of strategies) {
      const item = resolvedProductItems.find(
        (i) => i.productType === entry.entityType && i.productId === entry.productId
      );
      if (item) {
        map.set(`${entry.entityType}-${entry.productId}`, computeGrossPositionCounts(item, basketLookup));
      }
    }
    return map;
  }, [strategies, resolvedProductItems, basketLookup]);

  const perfFetchIdRef = useRef(0);

  const fetchPerf = options?.fetchProductPerformance ?? false;
  useEffect(() => {
    if (!fetchPerf || userId || strategies.length === 0 || isLoadingStrategies || resolvedIsLoadingProducts) return;
    const fetchId = ++perfFetchIdRef.current;
    const requests = strategies
      .map((s) => {
        const lookup = resolvedProductLookup.get(`${s.entityType}-${s.productId}` as ProductLookupKey);
        if (!lookup) return null;
        return {
          providerId: lookup.providerId,
          productType: s.entityType.toLowerCase() as PerformanceProductType,
          productId: s.productId,
        };
      })
      .filter(Boolean) as Array<{ providerId: number; productType: PerformanceProductType; productId: number }>;
    if (requests.length === 0) return;
    productPerformanceService.getPerformanceBatch(requests).then((results) => {
      if (fetchId !== perfFetchIdRef.current) return;
      const map: Record<string, PerformanceResponse | null> = {};
      for (const r of results) {
        map[`${r.productType.toUpperCase()}-${r.productId}`] = r.response;
      }
      setPerfByProduct(map);
    }).catch(() => {});
  }, [fetchPerf, userId, strategies, resolvedProductLookup, isLoadingStrategies, resolvedIsLoadingProducts]);

  const strategyIdProductMap: StrategyIdProductMap = useMemo(() => {
    const map: StrategyIdProductMap = new Map();
    for (const item of allHistoryItems) {
      if (map.has(item.investorStrategyId)) continue;
      const et = item.payload.entityType as string | undefined;
      const pid = item.payload.productId as number | undefined;
      if (et && pid != null) {
        map.set(item.investorStrategyId, { entityType: et, productId: pid });
      }
    }
    return map;
  }, [allHistoryItems]);

  const strategyInceptionDate = useMemo(() => {
    if (strategyFactors.length === 0) return null;
    return strategyFactors[0].t;
  }, [strategyFactors]);

  const strategyPerformanceStartDate = useMemo(() => {
    if (strategyFactors.length === 0) return null;
    const firstFactorMs = new Date(strategyFactors[0].t).getTime();
    if (Number.isNaN(firstFactorMs)) return strategyFactors[0].t;
    return new Date(firstFactorMs - V3_FACTOR_SLOT_MS).toISOString();
  }, [strategyFactors]);

  const strategyPerfMetrics = useMemo(() => {
    if (strategyFactors.length === 0) return null;

    const metrics = strategyFactors.map(f => ({
      metricDate: f.t,
      metricValue: f.f,
      metricName: "factor" as const,
    }));

    const anchor = new Date(strategyFactors[strategyFactors.length - 1].t);
    const earliestFactorDate = strategyFactors[0].t;
    const available = getV3AvailableFilters(earliestFactorDate, anchor);

    const computeForPeriod = (period: V3TimeFilter): number | null => {
      if (period === "All" || period === "Since inception") {
        return computeCumulativePerformance(metrics);
      }

      if (period !== "Today" && !available.includes(period)) {
        return null;
      }

      const entityStart = period === "Today" ? strategyPerformanceStartDate : strategyInceptionDate;
      const range = getV3RangeForFilter(period, anchor, entityStart);
      const fromMs = range.from ? new Date(range.from).getTime() : Number.NEGATIVE_INFINITY;
      const toMs = range.to ? new Date(range.to).getTime() : Number.POSITIVE_INFINITY;
      const filtered = metrics.filter((metric) => {
        const metricMs = new Date(metric.metricDate).getTime();
        return metricMs > fromMs && metricMs <= toMs;
      });

      return computeCumulativePerformance(filtered);
    };

    return {
      inception: computeForPeriod("Since inception"),
      returnToday: computeForPeriod("Today"),
      return1D: computeForPeriod("1 day"),
      return1M: computeForPeriod("1 month"),
      return3M: computeForPeriod("3 months"),
    };
  }, [strategyFactors, strategyInceptionDate, strategyPerformanceStartDate]);

  const isLoading = isLoadingStrategies || resolvedIsLoadingProducts;

  const refetchCurrentTab = useCallback(() => {
    fetchStrategies();
    fetchHistory();
    fetchStrategyFactors();
  }, [fetchStrategies, fetchHistory, fetchStrategyFactors]);

  return {
    strategies,
    reservedEntries,
    productLookup: resolvedProductLookup,
    productItems: resolvedProductItems,
    allHistoryItems,
    strategyIdProductMap,
    strategyFactors,

    isLoadingStrategies,
    isLoadingProducts: resolvedIsLoadingProducts,
    isLoadingHistory,
    isLoadingFactors,
    isLoading,

    strategyError,
    historyError,
    factorsError,

    totalWeight,
    basketLookup,
    positionCountsMap,
    strategyInceptionDate,
    strategyPerformanceStartDate,
    strategyPerfMetrics,
    perfByProduct,

    refetchStrategies: fetchStrategies,
    refetchProducts: fetchProducts,
    refetchCurrentTab,
  };
}
